import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Map as MapIcon, Loader2, Save, KeyRound } from 'lucide-react';
import { api } from '../lib/auth';
import { loadMapSettings } from '../lib/useMapSettings';
import { PROVIDER_LAYERS } from '../lib/mapTiles';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

const PROVIDERS = [
  ['esri', 'Esri', 'Sokak, uydu, açık/koyu gri tema — ücretsiz'],
  ['osm', 'OpenStreetMap', 'Ücretsiz açık harita'],
  ['carto', 'Carto', 'Açık / koyu tema — ücretsiz API anahtarı gerekir'],
  ['google', 'Google Haritalar', 'Yol, uydu, trafik — API anahtarı gerekir'],
];
const COLORS = [['depot_start', 'Depo → Start'], ['start_stop', 'Start → Stop (tur)'], ['stop_depot', 'Stop → Depo']];
const Box = ({ title, children }) => (
  <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
    <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h2>{children}
  </section>
);

export default function MapSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadMapSettings(true).then(setCfg); }, []);
  if (!cfg) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const toggleProv = (p) => {
    const providers = cfg.providers.includes(p) ? cfg.providers.filter((x) => x !== p) : [...cfg.providers, p];
    set({ providers, default_layer: providers.includes(cfg.default_layer.split(':')[0]) ? cfg.default_layer : (Object.keys(PROVIDER_LAYERS).find((id) => providers.includes(PROVIDER_LAYERS[id].provider)) || cfg.default_layer) });
  };
  const layerOpts = Object.keys(PROVIDER_LAYERS).filter((id) => cfg.providers.includes(PROVIDER_LAYERS[id].provider));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/map-settings', { providers: cfg.providers, default_layer: cfg.default_layer, fallback: cfg.fallback, default_sim_speed: cfg.default_sim_speed, traffic_default: cfg.traffic_default, leg_colors: cfg.leg_colors });
      setCfg(r.data.data);
      await loadMapSettings(true);
      toast.success('Harita ayarları kaydedildi');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (cfg.providers.length ? 'Kaydedilemedi' : 'En az bir harita sağlayıcısı seçin'));
    }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="map-settings-page">
      <div className="flex items-center gap-3">
        <MapIcon className="h-7 w-7" />
        <div><h1 className="text-2xl font-black">Harita Ayarları</h1><p className="text-sm text-slate-500">Start-Stop rota haritası ve simülasyonu için</p></div>
      </div>
      <Box title="Harita sağlayıcıları">
        <div className="grid sm:grid-cols-2 gap-2">
          {PROVIDERS.map(([k, l, d]) => {
            const avail = k === 'google' ? cfg.google_available : k === 'carto' ? cfg.carto_available : true;
            const locked = !avail;
            return (
              <label key={k} className={`flex items-start gap-2 rounded-xl border p-3 ${cfg.providers.includes(k) ? 'border-slate-900 bg-slate-50 dark:bg-slate-800' : 'border-slate-200 dark:border-slate-700'} ${locked ? 'opacity-60' : 'cursor-pointer'}`} data-testid={`map-provider-${k}`}>
                <input type="checkbox" disabled={locked} checked={cfg.providers.includes(k)} onChange={() => toggleProv(k)} className="mt-1" data-testid={`map-provider-${k}-check`} />
                <span><span className="block text-sm font-semibold">{l}</span><span className="block text-[11px] text-slate-500">{d}</span>
                  {(k === 'google' || k === 'carto') && <span className={`mt-1 inline-flex items-center gap-1 text-[10px] font-bold ${avail ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`map-${k}-key-status`}><KeyRound className="h-3 w-3" />{avail ? 'API anahtarı tanımlı' : 'API anahtarı eklenmemiş'}</span>}
                </span>
              </label>
            );
          })}
        </div>
        <label className="block text-sm">Varsayılan harita
          <select value={cfg.default_layer} onChange={(e) => set({ default_layer: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm" data-testid="map-default-layer">
            {layerOpts.map((id) => <option key={id} value={id}>{PROVIDER_LAYERS[id].label}</option>)}
          </select>
        </label>
        <label className="flex items-center justify-between gap-3 text-sm"><span><b>Otomatik yedeğe geçiş</b><span className="block text-[11px] text-slate-500">Harita yüklenemezse sıradaki sağlayıcıya geçer</span></span>
          <Switch checked={cfg.fallback} onCheckedChange={(v) => set({ fallback: v })} data-testid="map-fallback" /></label>
        {cfg.providers.includes('google') && (
          <label className="flex items-center justify-between gap-3 text-sm"><span><b>Trafik katmanı açık başlasın</b><span className="block text-[11px] text-slate-500">Google trafik yoğunluğu</span></span>
            <Switch checked={cfg.traffic_default} onCheckedChange={(v) => set({ traffic_default: v })} data-testid="map-traffic-default" /></label>
        )}
      </Box>
      <Box title="Simülasyon ve renkler">
        <div className="flex items-center gap-2 text-sm">Varsayılan simülasyon hızı
          {[1, 5, 20].map((v) => (
            <button key={v} type="button" onClick={() => set({ default_sim_speed: v })} data-testid={`map-sim-speed-${v}`}
              className={`h-8 px-3 rounded-lg text-xs font-bold border ${cfg.default_sim_speed === v ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>{v}x</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          {COLORS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 p-2 text-sm">
              <input type="color" value={cfg.leg_colors[k]} onChange={(e) => set({ leg_colors: { ...cfg.leg_colors, [k]: e.target.value } })} className="h-8 w-10 rounded cursor-pointer" data-testid={`map-color-${k}`} />{l}
            </label>
          ))}
        </div>
      </Box>
      <Button onClick={save} disabled={busy} data-testid="map-settings-save">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Kaydet</Button>
    </div>
  );
}
